import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import starEmpty from "../assets/star_empty.svg";
import starFilled from "../assets/star_filled.svg";
import loadingDots from "tenant/loading_dots.gif";
import "../styles/components/FinalFeedback.css";

interface FinalFeedbackProps {
  rating: number;
  setRating: (rating: number) => void;
  feedbackText: string;
  setFeedbackText: (text: string) => void;
  isLoading: boolean;
  onContinue: (skip: boolean) => void;
}

const FinalFeedback: React.FC<FinalFeedbackProps> = ({
  rating,
  setRating,
  feedbackText,
  setFeedbackText,
  isLoading,
  onContinue,
}) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'star' | 'text'>('star');
  const isStarPhase = phase === 'star';

  // Automatically transition to text phase when a star is selected
  useEffect(() => {
    if (rating > 0) {
      setPhase('text');
    }
  }, [rating]);

  const handleStarClick = (starValue: number) => {
    setRating(starValue);
  };

  const handleButtonClick = () => {
    onContinue(isStarPhase);
  };

  const buttonText = isStarPhase ? t("feedback.skip") : t("finalizeUser.continue");
  const buttonClassName = isStarPhase 
    ? 'feedback-continue-button feedback-skip-button' 
    : 'feedback-continue-button';


  if (isLoading) {
    return (
      <div className="feedback-wrapper">
        <div className="feedback-loading-overlay">
          <img src={loadingDots} alt={t("loadingSession.loadingAlt")} className="loading-dots" />
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-wrapper">
      <div className={`feedback-ui-container ${isStarPhase ? 'star-phase' : 'text-phase'}`}>
        <div className="feedback-header">
          <div className="feedback-header-text">{t("feedback.howWasExperience")}</div>
        </div>
        <div className="star-rating-container">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              className={`star-button ${star <= rating ? 'filled' : ''}`}
              onClick={() => handleStarClick(star)}
              aria-label={t("feedback.rateStar", { count: star })}
              disabled={isLoading}
            >
              <img 
                src={star <= rating ? starFilled : starEmpty} 
                alt={star <= rating ? t("feedback.filledStar") : t("feedback.emptyStar")}
                width="28"
                height="28"
              />
            </button>
          ))}
        </div>
        {!isStarPhase && (
          <>
            <div className="feedback-prompt-text">
              {rating === 5 
                ? t("feedback.awesomePrompt")
                : t("feedback.improvePrompt")}
            </div>
            <div className="feedback-textarea-container">
              <textarea
                className="feedback-textarea"
                placeholder={t("feedback.placeholder")}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={8}
                disabled={isLoading}
              />
            </div>
          </>
        )}
      </div>
      <button 
        className={buttonClassName}
        onClick={handleButtonClick}
        disabled={isLoading}
      >
        {buttonText}
      </button>
    </div>
  );
};

export default FinalFeedback;
